'use client';

import useEmblaCarousel from 'embla-carousel-react';
import { usePathname, useSearchParams, useRouter } from 'next/navigation';
import { useRef, useState } from 'react';
import { cn } from '@/shared/lib/cn';
import { CategoriesCategory } from './categories-category';
import { CategoriesScrollIndicators } from './categories-scroll-indicators';
import { CategoriesSubcategories } from './categories-subcategories';
import './categories.scss';
import { CategoriesProps } from './types';
import { categoriesVariants } from './variants';

export const CategoriesComponent = ({
    categories,
    variant,
    isSubcategoryVisible = true,
    isIconVisible,
    isNameVisible,
    className,
    ...props
}: CategoriesProps) => {
    const [selected, setSelected] = useState<string[]>([]);
    const listRef = useRef<HTMLDivElement>(null);
    const [emblaRef, emblaApi] = useEmblaCarousel({
        active: variant === 'carousel',
        dragFree: true,
        align: 'center',
        containScroll: 'keepSnaps',
    });
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();

    const changeQueryParam = (categoryId?: string) => {
        const params = new URLSearchParams(searchParams.toString());
        if (!categoryId) {
            params.delete('categoryId');
        } else {
            params.set('categoryId', categoryId);
        }
        router.push(`${pathname}?${params.toString()}`, {
            scroll: false,
        });
    };
    const toggleCategory = (categoryId: string, index: number) => {
        if (selected[0] === categoryId && selected.length === 1) {
            changeQueryParam(undefined);
            setSelected([]);
            return;
        }
        changeQueryParam(categoryId);
        setSelected([categoryId]);
        emblaApi?.scrollTo(index);
    };
    const toggleSubCategory = (categoryId: string, index: number) => {
        if (selected[index] === categoryId) {
            changeQueryParam(undefined);
            setSelected((prev) => prev.slice(0, index));
            return;
        }
        changeQueryParam(categoryId);
        setSelected((prev) => [...prev.slice(0, index), categoryId]);
    };

    return (
        <div className={cn(categoriesVariants({ variant }), className)} {...props}>
            <div className="categories__container">
                <CategoriesScrollIndicators emblaApi={emblaApi} disabled={variant !== 'carousel'}>
                    {({ firstRef, lastRef }) => (
                        <div className="categories__carousel" ref={emblaRef}>
                            <div className="categories__list" ref={listRef}>
                                {categories.map(({ subCategories: _, ...category }, index, arr) => (
                                    <CategoriesCategory
                                        ref={
                                            (index === 0 && firstRef) ||
                                            (index === arr.length - 1 && lastRef) ||
                                            undefined
                                        }
                                        variant={variant}
                                        key={category.id}
                                        isSelected={selected[0] === category.id}
                                        isIconVisible={isIconVisible}
                                        isNameVisible={isNameVisible}
                                        onClick={() => toggleCategory(category.id, index)}
                                        {...category}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </CategoriesScrollIndicators>
            </div>
            <CategoriesSubcategories
                isSubcategoryVisible={isSubcategoryVisible}
                categories={categories}
                selected={selected}
                index={0}
                toggleSubCategory={toggleSubCategory}
                isIconVisible={isIconVisible}
                isNameVisible={isNameVisible}
            />
        </div>
    );
};
